import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import type { ContractPaymentMethod } from "../types";
import RemoveCardDialog from "./remove-card-dialog";

const CARD_REMOVAL_ENABLED = false;

interface Props {
  contractId: string | number;
  methods: ContractPaymentMethod[];
}

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: 0.25 + index * 0.12,
      type: "spring" as const,
      stiffness: 260,
      damping: 24,
    },
  }),
};

const SavedCardList: FC<Props> = ({ contractId, methods }) => {
  const { t } = useTranslation(["capture-card"]);
  const [methodToRemove, setMethodToRemove] =
    useState<ContractPaymentMethod | null>(null);

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        {methods.map((method, index) => {
          const cardDetails = method.attributes.card_details;
          return (
            <motion.div
              key={method.id}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              whileHover={{ y: -3 }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 p-5 text-white shadow-lg"
            >
              {/* soft glow accent */}
              <div className="pointer-events-none absolute -right-16 -top-16 size-40 rounded-full bg-primary/30 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-10 size-40 rounded-full bg-sky-500/10 blur-3xl" />

              <div className="relative flex items-start justify-between">
                {/* chip */}
                <div className="h-7 w-9 rounded-md bg-gradient-to-br from-amber-200 to-amber-400/80 opacity-90" />
                <span className="text-xs font-semibold uppercase tracking-widest text-white/80">
                  {cardDetails?.brand}
                </span>
              </div>

              <div className="relative mt-6 flex items-center gap-3 text-lg tracking-[0.18em]">
                <span className="text-white/50">••••</span>
                <span className="text-white/50">••••</span>
                <span className="text-white/50">••••</span>
                <span className="font-medium">{cardDetails?.last_four}</span>
              </div>

              <div className="relative mt-5 flex items-end justify-between text-xs text-white/60">
                <div className="flex items-center gap-1.5">
                  <CreditCard className="size-3.5" />
                  <span>
                    {t("capture-card:capture-card-expires", "Expires")}{" "}
                    {cardDetails?.exp_month}/{cardDetails?.exp_year}
                  </span>
                </div>
                {CARD_REMOVAL_ENABLED && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-white/60 hover:bg-white/10 hover:text-red-400"
                    onClick={() => setMethodToRemove(method)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
      {CARD_REMOVAL_ENABLED && (
        <RemoveCardDialog
          contractId={contractId}
          method={methodToRemove}
          onOpenChange={(open) => {
            if (!open) setMethodToRemove(null);
          }}
        />
      )}
    </>
  );
};

export default SavedCardList;
